import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ArrowLeft, CheckCircle2, Brain, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLUB_META = {
  Analistas: { emoji: "🧠", color: "#A855F7" },
  Diplomatas: { emoji: "🤝", color: "#22C55E" },
  Sentinelas: { emoji: "🛡️", color: "#3B82F6" },
  Exploradores: { emoji: "🚀", color: "#EAB308" },
};

const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return err?.message || "Erro inesperado";
};

const PAGE = 7;

export default function MbtiTest() {
  const { user } = useAuth();
  const [version, setVersion] = useState(null);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user) { setEmail(user.email || ""); setName(user.name || ""); } }, [user]);

  const start = async (v) => {
    try {
      const r = await axios.get(`${API}/mbti-test/questions?version=${v}`);
      setData(r.data); setVersion(v); setAnswers({}); setPage(0); setResult(null);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const questions = data?.questions || [];
  const pages = Math.ceil(questions.length / PAGE);
  const slice = questions.slice(page * PAGE, page * PAGE + PAGE);
  const answered = Object.keys(answers).length;
  const pageDone = slice.every((q) => answers[q.id] !== undefined);
  const progress = questions.length ? Math.round(100 * answered / questions.length) : 0;

  const submit = async () => {
    if (!email.trim()) return toast.error("Indica o teu email KW para guardarmos o resultado.");
    setSaving(true);
    try {
      const body = { version, answers, email: email.trim(), name: name.trim() };
      const path = user ? "/mbti-test/submit" : "/mbti-test/submit-public";
      const r = user
        ? await axios.post(`${API}${path}`, body, { withCredentials: true })
        : await axios.post(`${API}${path}`, body);
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };

  const club = result?.club ? CLUB_META[result.club] : null;

  if (result) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="mbti-result">
        <div className="max-w-2xl mx-auto px-6 py-16 space-y-8">
          <div className="text-center">
            <CheckCircle2 className="w-10 h-10 text-[#22C55E] mx-auto mb-4" />
            <div className="kw-overline">O teu perfil</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter mt-2">
              {result.personality_label}
            </h1>
            <div className="font-mono text-2xl mt-2" style={{ color: club?.color }}>{result.mbti}</div>
          </div>

          {club && (
            <div className="border p-6 text-center" style={{ borderColor: club.color }} data-testid="mbti-club">
              <div className="text-5xl mb-3">{club.emoji}</div>
              <div className="kw-overline text-[10px]">O teu clube</div>
              <div className="font-display text-3xl font-black uppercase tracking-tight" style={{ color: club.color }}>
                {result.club}
              </div>
              <p className="text-xs text-[#737373] mt-3">
                Já ficou guardado no teu perfil da Growth League. Não tens de copiar nada.
              </p>
            </div>
          )}

          <div className="space-y-5">
            {result.axes.map((a) => (
              <div key={a.axis} data-testid={`axis-${a.axis}`}>
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-[#737373] mb-1.5">
                  <span className={a.pct_right < 50 ? "text-white font-bold" : ""}>{a.left_label}</span>
                  <span className="font-mono text-white">{a.pct_dominant}% {a.letter}</span>
                  <span className={a.pct_right >= 50 ? "text-white font-bold" : ""}>{a.right_label}</span>
                </div>
                <div className="h-2 bg-[#1A1A1A] relative">
                  <div className="absolute inset-y-0 w-px bg-[#333] left-1/2" />
                  <div className="h-full transition-all duration-700"
                    style={{
                      width: `${Math.abs(a.pct_right - 50)}%`,
                      marginLeft: a.pct_right >= 50 ? "50%" : `${a.pct_right}%`,
                      background: club?.color || "#E50000",
                    }} />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center pt-4 border-t border-[#1A1A1A]">
            {version === "short" ? (
              <button onClick={() => start("long")} className="kw-btn-secondary" data-testid="mbti-go-long">
                Confirmar com a versão longa (60 afirmações)
              </button>
            ) : (
              <p className="text-[10px] uppercase tracking-widest text-[#555]">
                Versão longa concluída · resultado mais fino
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!version) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="mbti-intro">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="kw-overline">KW Lead Growth League</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mt-2 leading-[0.95]">
            Qual é o teu<br /><span className="text-[#E50000]">perfil</span>?
          </h1>
          <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed mt-6">
            O teu perfil de personalidade define o teu <b className="text-white">Clube</b> na Growth League.
            Responde com honestidade — não há respostas certas, há a tua.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            {Object.entries(CLUB_META).map(([k, v]) => (
              <span key={k} className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 border"
                style={{ borderColor: v.color + "66", color: v.color }}>
                {v.emoji} {k}
              </span>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-10">
            <button onClick={() => start("short")} className="kw-card p-6 text-left kw-card-hover" data-testid="mbti-start-short">
              <Clock className="w-5 h-5 text-[#E50000] mb-3" />
              <div className="font-display text-2xl font-black uppercase tracking-tight">Versão curta</div>
              <div className="text-xs text-[#A3A3A3] mt-1">28 afirmações · cerca de 3 minutos</div>
              <div className="text-[10px] uppercase tracking-widest text-[#E50000] mt-3">Recomendada</div>
            </button>
            <button onClick={() => start("long")} className="kw-card p-6 text-left kw-card-hover" data-testid="mbti-start-long">
              <Brain className="w-5 h-5 text-[#A855F7] mb-3" />
              <div className="font-display text-2xl font-black uppercase tracking-tight">Versão longa</div>
              <div className="text-xs text-[#A3A3A3] mt-1">60 afirmações · cerca de 8 minutos</div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-3">Resultado mais fino</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="mbti-questions">
      <div className="sticky top-0 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#1A1A1A] z-10">
        <div className="max-w-2xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#737373]">
            <span>{version === "short" ? "Versão curta" : "Versão longa"}</span>
            <span data-testid="mbti-progress">{answered}/{questions.length}</span>
          </div>
          <div className="h-1 bg-[#1A1A1A] mt-2">
            <div className="h-full bg-[#E50000] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10 space-y-10">
        {slice.map((q, i) => (
          <div key={q.id} data-testid={`q-${q.id}`}>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-display text-lg font-black text-[#E50000]">{page * PAGE + i + 1}</span>
              <p className="text-base md:text-lg font-bold leading-snug">{q.text}</p>
            </div>
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] uppercase tracking-widest text-[#A855F7] w-16 shrink-0">Discordo</span>
              {(data.scale || []).map((s) => {
                const sel = answers[q.id] === s.value;
                const size = 10 + Math.abs(s.value) * 5;
                const tone = s.value === 0 ? "#A3A3A3" : s.value < 0 ? "#A855F7" : "#22C55E";
                return (
                  <button key={s.value} type="button" title={s.label}
                    onClick={() => setAnswers({ ...answers, [q.id]: s.value })}
                    className="rounded-full transition-all duration-150 shrink-0 hover:scale-110"
                    style={{
                      width: size + 16, height: size + 16,
                      border: `2px solid ${tone}`,
                      opacity: sel ? 1 : 0.65,
                      background: sel ? tone : "rgba(255,255,255,0.04)",
                      boxShadow: sel ? `0 0 0 4px ${tone}33` : "none",
                      transform: sel ? "scale(1.15)" : "scale(1)",
                    }}
                    data-testid={`${q.id}-${s.value}`} />
                );
              })}
              <span className="text-[10px] uppercase tracking-widest text-[#22C55E] w-16 shrink-0 text-right">Concordo</span>
            </div>
          </div>
        ))}

        {page === pages - 1 && (
          <div className="space-y-3 pt-6 border-t border-[#1A1A1A]">
            <div className="kw-overline text-[10px]">Para guardarmos no teu perfil</div>
            <input className="kw-input" placeholder="Nome completo" value={name}
              onChange={(e) => setName(e.target.value)} data-testid="mbti-name" />
            <input className="kw-input" type="email" placeholder="Email KW" value={email}
              onChange={(e) => setEmail(e.target.value)} data-testid="mbti-email" />
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          <button onClick={() => { setPage(Math.max(0, page - 1)); window.scrollTo({ top: 0 }); }}
            disabled={page === 0}
            className="kw-btn-secondary inline-flex items-center gap-2 disabled:opacity-30" data-testid="mbti-prev">
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          {page < pages - 1 ? (
            <button onClick={() => { setPage(page + 1); window.scrollTo({ top: 0 }); }} disabled={!pageDone}
              className="kw-btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="mbti-next">
              Seguinte <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={saving || answered < questions.length}
              className="kw-btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="mbti-submit">
              {saving ? "A calcular…" : "Ver o meu perfil"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
        {page === pages - 1 && answered < questions.length && (
          <p className="text-[10px] uppercase tracking-widest text-[#EAB308] text-center">
            Faltam {questions.length - answered} afirmações
          </p>
        )}
      </div>
    </div>
  );
}
